import { useEffect, useMemo } from 'react';
import { Modal, Form, Select, Checkbox, Tag, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The territory being transferred (id, code, name, level). */
  territory: any;
  /** Current primary SP, if any: { userId, fullName }. */
  currentPrimary?: { userId: string; fullName: string } | null;
}

/**
 * Move a territory's PRIMARY ownership from the current SP to another, optionally taking
 * the outgoing SP's customers (in this territory's subtree) along. Open orders/invoices
 * stay with each customer.
 */
export function TransferTerritoryModal({ open, onClose, territory, currentPrimary }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const myRole = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);

  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
    enabled: open,
  });

  // SH may only transfer to their own team; admins/SA see all. Never offer the current primary.
  const options = useMemo(() => {
    let list: any[] = (spUsers?.data ?? []).filter((u: any) => u.status === 'active'); // never offer a deactivated SP
    if (myRole === 'SALES_HEAD') list = list.filter((u) => String(u.reportsTo) === String(myId));
    return list
      .filter((u) => String(u.id) !== String(currentPrimary?.userId))
      .map((u) => ({ value: String(u.id), label: u.fullName }));
  }, [spUsers, myRole, myId, currentPrimary]);

  useEffect(() => {
    if (open) form.setFieldsValue({ toUserId: undefined, moveCustomers: true });
  }, [open, form]);

  const transfer = useMutation({
    mutationFn: (vals: any) => request({
      url: '/territory-assignments/transfer', method: 'POST',
      data: {
        territoryId: String(territory.id),
        toUserId: vals.toUserId,
        fromUserId: currentPrimary?.userId ? String(currentPrimary.userId) : undefined,
        moveCustomers: !!vals.moveCustomers,
      },
    }),
    onSuccess: (res: any) => {
      const moved = res?.moved ?? 0;
      message.success(moved ? `Transferred — ${moved} customer(s) moved` : 'Territory transferred');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to transfer'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={territory ? `Transfer ${territory.code} · ${territory.name}` : 'Transfer territory'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={transfer.isPending} onClick={() => form.submit()}>Transfer</Button>
        </>
      }
    >
      {territory && (
        <Form form={form} layout="vertical" onFinish={(v) => transfer.mutate(v)}>
          <div style={{ marginBottom: 14 }}>
            From: {currentPrimary ? <Tag tone="green">{currentPrimary.fullName}</Tag> : <Tag>unassigned</Tag>}
          </div>
          <Form.Item name="toUserId" label="Transfer to Sales Person" rules={[{ required: true, message: 'Pick the receiving Sales Person' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Select sales person"
              notFoundContent={myRole === 'SALES_HEAD' ? 'No other sales persons in your team' : 'None found'}
              options={options} />
          </Form.Item>
          <Form.Item name="moveCustomers" valuePropName="checked"
            extra="Move the outgoing SP's retailers & farmers in this territory to the new SP.">
            <Checkbox label="Move owned customers" />
          </Form.Item>
          <Alert type="info" title="The new SP becomes the primary owner of customers created here from now on." />
        </Form>
      )}
    </Modal>
  );
}
